import React, { ChangeEvent, FC, Fragment, useRef } from 'react';
import { Box, Divider, Switch, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  ClientPageMapping,
  useCreateClientPageMappingMutation,
  useDeleteClientPageMappingMutation,
  useGetClientPageMappingsQuery,
} from 'api';
import { useNotify, useProtectedClient } from 'hooks';
import { useCreateClientPageMapping, useDeleteClientPageMapping } from 'features/admin/updaters';

const CompanyPages: FC = () => {
  const { t } = useTranslation();
  const client = useProtectedClient();
  const notify = useNotify();

  const latestToggledPage = useRef<string>('');

  const { data } = useGetClientPageMappingsQuery({ id: client.id });
  const pages = orderBy(data?.getClientPageMappings, 'connected_count', 'desc');

  const createClientPageMapping = useCreateClientPageMapping({ id: client.id });
  const { mutate: createPageMapping, isLoading: createIsLoading } =
    useCreateClientPageMappingMutation({
      onSuccess: () =>
        notify.info({
          message: t('admin:pageMappingToggleOn', { name: latestToggledPage.current }),
        }),
      onError: notify.mutationError,
      ...createClientPageMapping,
    });

  const deleteClientPageMapping = useDeleteClientPageMapping({ id: client.id });
  const { mutate: deletePageMapping, isLoading: deleteIsLoading } =
    useDeleteClientPageMappingMutation({
      onSuccess: () =>
        notify.info({
          message: t('admin:pageMappingToggleOff', { name: latestToggledPage.current }),
        }),
      onError: notify.mutationError,
      ...deleteClientPageMapping,
    });

  function onChangeSwitch(event: ChangeEvent<HTMLInputElement>, page: ClientPageMapping) {
    const { name, linkedin_page_id, token_ids } = page;
    latestToggledPage.current = name;
    event.target.checked
      ? createPageMapping({ data: { name, linkedin_page_id, token_ids } })
      : deletePageMapping({ linkedin_page_id });
  }

  function renderPages(page: ClientPageMapping) {
    const { name, linkedin_page_id, active, connected_count } = page;

    return (
      <Fragment key={linkedin_page_id}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid xs={4}>
            <Typography variant="h6">{name}</Typography>
          </Grid>

          <Grid xs={4} style={{ textAlign: 'center' }}>
            <Typography variant="h6">{connected_count}</Typography>
          </Grid>

          <Grid xs={4} style={{ textAlign: 'center' }}>
            <Switch
              name={'data-pulling'}
              checked={active}
              onChange={e => onChangeSwitch(e, page)}
              disabled={latestToggledPage.current === name && (createIsLoading || deleteIsLoading)}
            />
          </Grid>
        </Grid>

        <Box marginY={2}>
          <Divider />
        </Box>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Typography variant="subtitle1">{t('admin:companyPagesDescription')}</Typography>
      <Box marginTop={7}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid xs={4}>
            <Typography variant="subtitle2">Page Title</Typography>
          </Grid>

          <Grid xs={4}>
            <Typography variant="subtitle2" style={{ textAlign: 'center' }}>
              Users Connected
            </Typography>
          </Grid>

          <Grid xs={4} style={{ textAlign: 'center' }}>
            <Typography variant="subtitle2">Data Pulling</Typography>
          </Grid>
        </Grid>
      </Box>

      <Box marginY={2}>
        <Divider />
      </Box>

      {pages.map(page => renderPages(page))}
    </Fragment>
  );
};

export default CompanyPages;
